function arraysEqual(arr1: any, arr2: any) {
  if (arr1.length !== arr2.length) return false;

  // eslint-disable-next-line no-plusplus
  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) return false;
  }

  return true;
}

export const getDirtyValues = (values: any, dirtyFields: any) => {
  return Object.fromEntries(
    Object.entries(values).filter(([key]) => key in dirtyFields)
  );
};

export const floatingNumberComma = (
  value: number | string = "0",
  places: number = 2
): string => {
  if (value === 0 || value === "0" || isNaN(value as number)) {
    return "0.00";
  }
  const values: string[] = Number(value)?.toFixed(places)?.split(".") || [
    "0",
    "00",
  ];
  return `${Number(values[0])?.toLocaleString("en-US")}${
    values[1] ? `.${values[1]}` : ""
  }`;
};

export const fixEncoding = (str: string) => {
  try {
    // Convert wrongly-decoded UTF-8 string into bytes
    const bytes = new Uint8Array(str.split("").map((c) => c.charCodeAt(0)));

    // Decode bytes back as Windows-1252
    return new TextDecoder("windows-1252").decode(bytes);
  } catch {
    return str;
  }
};

export function DDToMM(dateStr: string) {
  if (!dateStr) return "";

  const [day, month, year] = dateStr.split("-");
  return `${month}-${day}-${year}`;
}

export function YYToMM(dateStr: string) {
  // 31-12-2024 - > 12-31-2024
  if (!dateStr) return "";

  const [year, day, month] = dateStr.split("-");
  return `${month}-${day}-${year}`;
}
export function YYYYMMDDToMM(dateStr: string) {
  // 2024-12-31 - > 12-31-2024
  if (!dateStr) return "";

  const [year, month, day] = dateStr.split("-");
  return `${month}-${day}-${year}`;
}

// export const numberComma = (
//   value: number | string = "0",
//   isSingleDigit: boolean = false
// ): string => {
//   const numericValue = Number(value);

//   if (numericValue === 0) {
//     return "0";
//   }

//   // Apply single digit formatting only when `isSingleDigit` is true
//   if (isSingleDigit && numericValue > 0 && numericValue < 10) {
//     return `0${numericValue}`;
//   }

//   // For all other cases, use locale formatting
//   return numericValue.toLocaleString("en-US");
// };

export const numberComma = (
  value: number | string = "0",
  isSingleDigit: boolean = false,
  decimals: number = 2,
  decimalsRequired: boolean = true // is decimals required
): string => {
  const numericValue = Number(value);

  if (isNaN(numericValue)) return "0";

  if (numericValue === 0) {
    return decimalsRequired && decimals > 0
      ? numericValue.toFixed(decimals)
      : "0";
  }

  // Add a leading zero for single-digit numbers (if requested)
  if (isSingleDigit && numericValue > 0 && numericValue < 10) {
    return `0${
      decimalsRequired
        ? numericValue.toFixed(decimals)
        : numericValue.toString()
    }`;
  }

  // Locale formatting
  return numericValue.toLocaleString("en-US", {
    minimumFractionDigits: decimalsRequired ? decimals : 0,
    maximumFractionDigits: decimalsRequired ? decimals : 0,
  });
};

export function formatAmount(amount: string, places: number = 0) {
  if (amount === "") {
    return "-";
  }
  const num = Number(amount);
  if (Number.isNaN(num)) {
    return "-";
  }

  if (num < 1_000) {
    // Show raw number (with optional decimal places)
    return num.toFixed(places ?? 0);
  } else if (num < 1_000_000) {
    const thousand = num / 1_000;
    return `${thousand.toFixed(places ?? 1)}K`;
  } else {
    const million = num / 1_000_000;
    return `${million.toFixed(places ?? 2)}M`;
  }
}

export function errorHandler(error: any) {
  return {
    error: error?.response?.data?.messageData?.message?.length
      ? {
          ...error?.response?.data?.messageData,
          status: error?.response?.status,
        }
      : error?.response?.data?.message && error?.response?.data?.message?.length
        ? {
            heading: "Something went wrong!",
            message: error?.response?.data?.message,
            status: error?.response?.status,
          }
        : {
            heading: "Something went wrong!",
            message: "Internal server error, Please contact support!",
            status: error?.response?.status || 0,
          },
  };
}

export function getOrdinalSuffix(number: any) {
  const suffixes = ["th", "st", "nd", "rd"];
  const value = number % 100;

  if (value >= 11 && value <= 13) {
    return number + suffixes[0];
  }

  switch (value % 10) {
    case 1:
      return number + suffixes[1];
    case 2:
      return number + suffixes[2];
    case 3:
      return number + suffixes[3];
    default:
      return number + suffixes[0];
  }
}

export const getExpiryTime = (milliSec: number) => {
  const expireTime = Date.now() + milliSec; // JWT expire * 1000
  return expireTime;
};

export const isSmallDifference = (arr: any, key: string, diff: number) => {
  const totals = arr?.map((obj: any) => obj?.[key]);
  const max = Math?.max(...totals);
  const min = Math?.min(...totals);
  return max - min < diff;
};

export const getEmptyFields = (values: any, dirtyFields: any) => {
  return Object.fromEntries(
    Object.entries(values)
      .filter(
        ([key, value]) =>
          key in dirtyFields &&
          (value === null || value === "" || value === undefined)
      )
      .map(([key]) => [key, null])
  );
};

// function differenceInterval(diff: any, tens = true, hundreds = true) {
//   if (diff < 10 && tens) return 10;
//   if (diff < 100 && hundreds) return 100;
//   if (diff < 1000) return 1000;
//   if (diff < 10000) return 10000;
//   if (diff < 100000) return 100000;
//   if (diff < 1000000) return 100000;
//   return 10;
// }

// export function checkDifference(array: any) {
//   // Extract the 'total' values from the array of objects
//   const totals = array?.map((item: any) => item?.total || 0);

//   // Find the highest and lowest 'total' values
//   const maxTotal = Math.max(...(totals || [0]));
//   const minTotal = Math.min(...(totals || [0]));

//   // Calculate the difference between the highest and lowest 'total'
//   const difference = maxTotal - minTotal;
//   if (maxTotal >= 1000000) {
//     return differenceInterval(difference, false, false);
//   } else if (maxTotal >= 1000) {
//     return differenceInterval(difference);
//   } else {
//     return 0; // If no specific condition is met, return the actual difference
//   }
// }

export function checkDifference(arr: any) {
  const data = arr?.map((item: any) => item?.total || 0);
  if (data.length === 0) return 1;

  let minVal = Math.min(...data);
  let maxVal = Math.max(...data);

  // Handle cases where all values are the same
  if (minVal === maxVal) {
    if (minVal === 0) {
      maxVal = 10;
    } else {
      const padding = Math.abs(minVal) * 0.1;
      minVal -= padding;
      maxVal += padding;
    }
  }

  const range = niceNumber(maxVal - minVal);

  // Adjust the number of tick marks based on the range
  let tickCount = 10;
  if (range < 1) tickCount = 5;
  if (range > 1e5) tickCount = 5;

  let interval = niceNumber(range / (tickCount - 1), true);

  // Ensure the interval is not zero
  if (interval === 0) {
    const magnitude = Math.floor(Math.log10(Math.abs(minVal)));
    interval = Math.pow(10, magnitude - 1);
  }

  let niceMin = Math.floor(minVal / interval) * interval;
  let niceMax = Math.ceil(maxVal / interval) * interval;

  // Adjust for very small ranges
  if (niceMax === niceMin) {
    if (niceMin === 0) {
      niceMax = interval;
    } else {
      const padding = Math.abs(niceMin) * 0.1;
      niceMin -= padding;
      niceMax += padding;
    }
  }

  return interval;
}

function niceNumber(value: number, round = false) {
  if (value === 0) return 0;

  const exponent = Math.floor(Math.log10(Math.abs(value)));
  const fraction = Math.abs(value) / Math.pow(10, exponent);
  let niceFraction;

  if (round) {
    if (fraction < 1.5) niceFraction = 1;
    else if (fraction < 3) niceFraction = 2;
    else if (fraction < 7) niceFraction = 5;
    else niceFraction = 10;
  } else {
    if (fraction <= 1) niceFraction = 1;
    else if (fraction <= 2) niceFraction = 2;
    else if (fraction <= 5) niceFraction = 5;
    else niceFraction = 10;
  }

  return niceFraction * Math.pow(10, exponent) * Math.sign(value);
}

export function removeDuplicates(array: any, keys: any) {
  const keyString = Array.isArray(keys) ? keys.join("|") : keys;

  return Array.from(
    new Map(
      array.map((item: any) => [
        Array.isArray(keys)
          ? keys.map((key) => item[key]).join("|")
          : item[keyString],
        item,
      ])
    ).values()
  );
}

export function getAllBasicPermissions() {
  return {
    about: {
      access: true,
      projectAttributes: {
        access: true,
      },
      tradeOff: {
        access: true,
        partial: [],
      },
      map: {
        access: true,
      },
    },
    status: {
      access: true,
    },
    stakeholders: {
      access: true,
      partial: [
        {
          name: "overallSummary",
          access: true,
        },
        {
          name: "stakeholderSummary",
          access: true,
        },
        {
          name: "projectProponents",
          access: true,
        },
        {
          name: "coImplementationPartners",
          access: true,
        },
        {
          name: "others",
          access: true,
        },
      ],
    },
    risk: {
      access: true,
      chart: {
        access: true,
      },
      report: {
        access: false,
        downloadable: true,
      },
      riskMitigations: {
        access: true,
        partial: [],
      },
    },
    financials: {
      access: true,
      cashflow: {
        access: true,
        operating: true,
        investing: true,
        financing: true,
        endingBalance: true,
      },
      deals: {
        access: true,
        partial: [],
      },
    },
    technical: {
      access: true,
      carbonSchedule: {
        access: true,
      },
      impact: {
        access: true,
      },
      carbonModel: {
        access: true,
      },
    },
    documents: {
      access: true,
      partial: [],
    },
    gallery: {
      access: true,
      partial: [],
    },
    faq: {
      access: true,
    },
  };
}
export function formatDateWithTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  // Check if the parsed date is valid
  if (isNaN(date.getTime())) {
    return ""; // Handle invalid date
  }
  const diffInMilliseconds = now.getTime() - date.getTime();
  const diffInMinutes = Math.floor(diffInMilliseconds / (1000 * 60));
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.floor(diffInHours / 24);
  const diffInMonths =
    now.getMonth() -
    date.getMonth() +
    12 * (now.getFullYear() - date.getFullYear());
  const diffInYears = Math.floor(diffInMonths / 12);

  if (diffInMinutes < 60) {
    // Less than an hour
    return `${diffInMinutes}m ago`;
  } else if (diffInHours < 24) {
    // Less than a day
    return `${diffInHours}h ago`;
  } else if (diffInDays < 30) {
    // Less than a month
    return `${diffInDays}d ago`;
  } else if (diffInMonths < 12) {
    // Less than a year
    return `${diffInMonths}M ago`;
  } else {
    // Years
    return `${diffInYears}y ago`;
  }
}
export function formatDateWithTimeVariant(dateString: string): any {
  const date = new Date(dateString);
  const now = new Date();
  // Check if the parsed date is valid
  if (isNaN(date.getTime())) {
    return {
      text: "",
      status: "Invalid",
    };
  }
  const diffInMilliseconds = date.getTime() - now.getTime();
  const diffInMinutes = Math.floor(diffInMilliseconds / (1000 * 60));
  const diffInHours = Math.floor(diffInMinutes / 60);
  const diffInDays = Math.ceil(diffInHours / 24);
  const diffInMonths =
    date.getMonth() -
    now.getMonth() +
    12 * (date.getFullYear() - now.getFullYear());
  const diffInYears = Math.floor(diffInMonths / 12);

  if (diffInMinutes < 0) {
    if (diffInMinutes > -60) {
      // Less than an hour overdue
      return {
        text: `${Math.abs(diffInMinutes)}m overdue`,
        status: "Overdue",
      };
    } else if (diffInHours > -24) {
      // Less than a day overdue
      return {
        text: `${Math.abs(diffInHours)}h overdue`,
        status: "Overdue",
      };
    } else if (diffInDays > -30) {
      // Less than a month overdue
      return {
        text: `${Math.abs(diffInDays)}d overdue`,
        status: "Overdue",
      };
    } else if (diffInMonths > -12) {
      // Less than a year overdue
      return {
        text: `${Math.abs(diffInMonths)}m overdue`,
        status: "Overdue",
      };
    } else {
      // Years overdue
      return {
        text: `${Math.abs(diffInYears)}y overdue`,
        status: "Overdue",
      };
    }
  } else {
    if (diffInMinutes < 60) {
      // Less than an hour upcoming
      return {
        text: `${diffInMinutes} m left`,
        status: "TODO",
      };
    } else if (diffInHours < 24) {
      // Less than a day upcoming
      return {
        text: `${diffInHours}h left`,
        status: "TODO",
      };
    } else if (diffInDays < 30) {
      // Less than a month upcoming
      return {
        text: `${diffInDays}d left`,
        status: "TODO",
      };
    } else if (diffInMonths < 12) {
      // Less than a year upcoming
      return {
        text: `${diffInMonths}m left`,
        status: "TODO",
      };
    } else {
      // Years upcoming
      return {
        text: `${diffInYears}y left`,
        status: "TODO",
      };
    }
  }
}

export const randomStringGen = (
  type: "AlphaNumeric" | "Password" | "Alphabet",
  length = 10
) => {
  const charType = {
    AlphaNumeric:
      "0123456789abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ",
    Password:
      "0123456789abcdefghijklmnopqrstuvwxyz!@#$%^&*()ABCDEFGHIJKLMNOPQRSTUVWXYZ",
    Alphabet: "abcdefghijklmnopqrstuvwxyzABCDEFGHIJKLMNOPQRSTUVWXYZ",
  };
  const chars = charType[type];
  const loopLength = type === "Password" ? length - 3 : length;
  let randomString = "";
  for (let i = 0; i <= loopLength; i++) {
    const randomNumber = Math.floor(Math.random() * chars.length);
    randomString += chars.substring(randomNumber, randomNumber + 1);
  }
  if (type === "Password") {
    const paswordChars = [
      "0123456789",
      "!@#$%^&*()",
      "ABCDEFGHIJKLMNOPQRSTUVWXYZ",
      "abcdefghijklmnopqrstuvwxyz",
    ];
    paswordChars.map((chars) => {
      const randomNumber = Math.floor(Math.random() * chars.length);
      randomString += chars.substring(randomNumber, randomNumber + 1);
    });
  }
  return randomString;
};

export function convertSecondsToMinutes(seconds: any): string {
  if (!seconds) {
    return "-:-";
  }
  const totalSeconds = Number(seconds);

  // Handle cases where the input is not a valid number
  if (isNaN(totalSeconds) || totalSeconds < 0) {
    return "00:00"; // Default return value for invalid or negative input
  }

  const minutes = Math.floor(totalSeconds / 60);
  const remainingSeconds = Math.floor(totalSeconds % 60);

  // Pad minutes and seconds with leading zeros if necessary
  const formattedMinutes = String(minutes).padStart(2, "0");
  const formattedSeconds = String(remainingSeconds).padStart(2, "0");

  return `${formattedMinutes}:${formattedSeconds}`;
}

export function timeAgo(lastVisit: string): string | null {
  if (!lastVisit) {
    return null;
  }
  const now = new Date();
  const visitDate = new Date(lastVisit);

  const differenceInSeconds = Math.floor(
    (now.getTime() - visitDate.getTime()) / 1000
  );

  if (differenceInSeconds < 60) {
    return `${differenceInSeconds} seconds ago`;
  }

  const differenceInMinutes = Math.floor(differenceInSeconds / 60);
  if (differenceInMinutes < 60) {
    return `${differenceInMinutes} mins ago`;
  }

  const differenceInHours = Math.floor(differenceInMinutes / 60);
  if (differenceInHours < 24) {
    return `${differenceInHours} hours ago`;
  }

  const differenceInDays = Math.floor(differenceInHours / 24);
  if (differenceInDays < 7) {
    return `${differenceInDays} days ago`;
  }

  const differenceInWeeks = Math.floor(differenceInDays / 7);
  if (differenceInWeeks < 4) {
    return `${differenceInWeeks} weeks ago`;
  }

  const differenceInMonths = Math.floor(differenceInDays / 30);
  if (differenceInMonths < 12) {
    return `${differenceInMonths} months ago`;
  }

  const differenceInYears = Math.floor(differenceInMonths / 12);
  return `${differenceInYears} years ago`;
}

export function isFileViewSupported(type: string): boolean {
  const supportedFileTypes = [
    "pdf",
    "jpeg",
    "jpg",
    "png",
    "gif",
    "webp",
    "xlsx",
    "xlsm",
    "xls",
    "pptx",
    "ppsx",
    "ppt",
    "docx",
    "doc",
    "txt",
    "csv",
  ];
  return supportedFileTypes.includes(type);
}

export function formatYearRanges(years: number[]): string {
  if (!years.length) return "";

  // sort to make sure order is correct
  const sorted = [...new Set(years)].sort((a, b) => a - b);

  const result: string[] = [];
  let start = sorted[0];
  let prev = sorted[0];

  for (let i = 1; i <= sorted.length; i++) {
    const current = sorted[i];

    if (current === prev! + 1) {
      // keep extending the range
      prev = current;
    } else {
      // range ends here
      if (start === prev) {
        result.push(`${start}`);
      } else {
        result.push(`${start}-${prev}`);
      }

      // start new range
      start = current;
      prev = current;
    }
  }

  return result.join("; ");
}

// Store references to opened windows globally
const windowRefs: { [key: string]: Window | null } = {};

/**
 * Opens a URL in a new tab or focuses an existing tab with the same name
 * @param url - The URL to open
 * @param windowName - A unique name for the window/tab
 * @returns The window reference
 */
export function openOrFocusTab(url: string, windowName: string): Window | null {
  // Check if window reference exists and is not closed
  if (windowRefs[windowName] && !windowRefs[windowName]?.closed) {
    // Focus existing window
    windowRefs[windowName]?.focus();
    return windowRefs[windowName] || null;
  } else {
    // Open new window and store reference
    const newWindow = window.open(url, windowName);
    windowRefs[windowName] = newWindow;
    return newWindow;
  }
}

/**
 * Clears all localStorage items except for specified keys to preserve
 * @param keysToPreserve - Array of localStorage keys that should not be cleared
 * @default ['cookie-consent'] - Preserves cookie consent by default
 */
export function clearLocalStorageExcept(
  keysToPreserve: string[] = ["cookie-consent"]
): void {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }

  // Store values of keys to preserve
  const preservedValues: { [key: string]: string | null } = {};
  keysToPreserve.forEach((key) => {
    preservedValues[key] = localStorage.getItem(key);
  });

  // Clear all localStorage
  localStorage.clear();

  // Restore preserved values
  Object.entries(preservedValues).forEach(([key, value]) => {
    if (value !== null) {
      localStorage.setItem(key, value);
    }
  });
}
